import axios from 'axios'
import { i18n } from '@/main.js' 

export default({
    state: {
        questions: [], // List of currently viewing questions (will always be a list of questions in a topic or in a test)
        question: null, // Question currently viewing/editing
        questionIdx: null, // Index of current question in questions array (used for moving forward/backward between questions)

        viewQuestionDialog: false,
        createQuestionDialog: false,

        // Values for editing a question:
        editing: false,
        removing: false,
        newTitle: '',
        newText: '',
        newAnswers: [],
        newCorrect: null,
        newNotes: '',
        newImage: null,
        newBasic: null,
        newTopicId: null,

        // File used for uploading image
        file: null,
    },

    getters: {
        getQuestion(state){ return state.question },
        getQuestions(state){ return state.questions },
        getQuestionIdx(state){ return state.questionIdx },

        getViewQuestionDialog(state) { return state.viewQuestionDialog },
        getCreateQuestionDialog(state) { return state.createQuestionDialog },

        isEditingQuestion(state){ return state.editing },
        isRemovingQuestion(state){ return state.removing },
        getQuestionTitle(state){ return state.newTitle },
        getQuestionText(state){ return state.newText },
        getQuestionAnswers(state){ return state.newAnswers },
        getQuestionCorrect(state){ return state.newCorrect },
        getQuestionNotes(state){ return state.newNotes },
        getQuestionImage(state){ return state.newImage },
        getQuestionBasic(state){ return state.newBasic },
        getQuestionTopicId(state){ return state.newTopicId },

        getFile(state){ return state.file },
    },

    mutations: {
        setQuestions(state, value){ state.questions = value },
        setQuestion(state, value){
            state.questionIdx = state.questions.findIndex(question => question.id == value.id)
            state.questions[state.questionIdx] = value
            state.question = value
        },
        nextQuestion(state){
            state.questionIdx += 1
            state.question = state.questions[state.questionIdx]
        },
        previousQuestion(state){
            state.questionIdx -= 1
            state.question = state.questions[state.questionIdx]
        },

        setViewQuestionDialog(state, value){ state.viewQuestionDialog = value },
        setCreateQuestionDialog(state, value){ state.createQuestionDialog = value },

        setEditingQuestion(state, value){ state.editing = value },
        setRemovingQuestion(state, value){ state.removing = value },
        setQuestionTitle(state, value){ state.newTitle = value },
        setQuestionText(state, value){ state.newText = value },
        setQuestionAnswers(state, value){ state.newAnswers = value },
        setQuestionCorrect(state, value){ state.newCorrect = value },
        setQuestionNotes(state, value){ state.newNotes = value },
        setQuestionImage(state, value){ state.newImage = value },
        setQuestionBasic(state, value){ state.newBasic = value },
        setQuestionTopicId(state, value){ state.newTopicId = value },

        // Set editing question values to empty. Used to create a new question
        newQuestion(state, topicId) {
            state.question = {
                title: null,
                text: null,
                answers: [ // Para cambiar el número de respuestas posibles modificar este array. El backend y el resto de vistas funcionarán bien con cualquier cantidad
                    { position: 1, text: null, questionId: null, },
                    { position: 2, text: null, questionId: null, },
                    { position: 3, text: null, questionId: null, },
                    { position: 4, text: null, questionId: null, },
                    { position: 5, text: null, questionId: null, },
                ],
                correctAnswer: 1,
                notes: null,
                basic: true,
                topicId: topicId,
                image: false,
                imageString: null,
            }
            state.file = null
        },

        // Set editing question values to current question values. Used when editing a question or creating one from another
        resetQuestionValues(state) {
            state.newTitle = state.question.title
            state.newText = state.question.text
            state.newAnswers = JSON.parse(JSON.stringify(state.question.answers)) // Duplicate answers array
            state.newCorrect = state.question.correctAnswer
            state.newNotes = state.question.notes
            state.newImage = state.question.imageString
            state.newBasic = state.question.basic
            state.newTopicId = state.question.topicId

            state.file = null
        },

        setFile(state, value){ state.file = value },
    },

    actions: {
        getQuestion({ commit }, questionId){
            commit('setLoading')
            return new Promise((resolve, reject) => {
                axios.get('/questions/' + questionId)
                .then((response) =>{
                    commit('setQuestion', response.data)
                    resolve(response.data)
                })
                .catch(() => {
                    reject()
                })
                .then(() => {
                    commit('unsetLoading')
                })
            })
        },

        getQuestionsByTopic({ commit }, {topicId, filter}){
            commit('setLoading')
            commit('setQuestions', [])
            return new Promise(() => {
                axios.get('/questions/topic/' + topicId, {
                    params: { filter }
                }).then((response) =>{
                    commit('setQuestions', response.data)
                })
                .catch(() => {})
                .then(() => {
                    commit('unsetLoading')
                })
            })
        },

        updateQuestion({ state, commit, dispatch }){
            commit('setLoading')

            var newQuestion = {...state.question}

            newQuestion.title = state.newTitle
            newQuestion.text = state.newText
            newQuestion.notes = state.newNotes
            newQuestion.correctAnswer = state.newCorrect
            newQuestion.answers = state.newAnswers
            newQuestion.imageString = state.newImage
            newQuestion.basic = state.newBasic
            newQuestion.topicId = state.newTopicId
            if (newQuestion.imageString)
                newQuestion.image = true

            return new Promise(() => {
                axios.put('/questions', newQuestion)
                .then(() => {
                    state.editing = false
                    commit('setSnackBar', {value: true, text: i18n.t('questionUpdated'), color: 'green'})
                    dispatch('getTopics')
                    dispatch('getQuestionsByTopic', { topicId: state.question.topicId })
                    dispatch('getQuestion', state.question.id)
                    .then((question) => {
                        if (question.image)
                            dispatch('getImage', question.id)
                    })
                })
                .catch(() => {})
                .then(() => {
                    commit('unsetLoading')
                })
            })
        },

        createQuestion({ state, commit, dispatch }){
            commit('setLoading')
            return new Promise(() => {
                axios.post('/questions', state.question)
                .then(() => {
                    commit('setCreateQuestionDialog', false)
                    commit('setSnackBar', {value: true, text: i18n.t('questionCreated'), color: 'green'})
                    dispatch('getTopics')
                    dispatch('getQuestionsByTopic', { topicId: state.question.topicId })
                })
                .catch(() => {})
                .then(() => {
                    commit('unsetLoading')
                })
            })
        },

        removeQuestion({ state, commit, dispatch }){
            commit('setLoading')
            return new Promise(() => {
                axios.delete('/questions/' + state.question.id)
                .then(() => {
                    commit('setViewQuestionDialog', false)
                    commit('setSnackBar', {value: true, text: i18n.t('questionRemoved'), color: 'green'})
                    dispatch('getTopics')
                    dispatch('getQuestionsByTopic', { topicId: state.question.topicId })
                })
                .catch(() => {})
                .then(() => {
                    commit('unsetLoading')
                })
            })
        },

        getImage({ commit, state }, questionId) {
            commit('setLoading')
            commit('setQuestionImage', null)
            return new Promise(() => {
                axios.get('/questions/' + questionId + '/image')
                .then((response) => {
                    state.question.imageString = response.data
                })
                .catch(() => {})
                .then(() => {
                    commit('unsetLoading')
                })
            })
        },
    },
});
